import { connect } from "react-redux";

import { Dispatch } from "redux";
import { KeyboardType } from "../../constants/keyboardTypes";
import { Routes } from "../../constants/routes";
import {
    finishedPostalCodeConfirmation,
    cancelPostalCodeConfirmation,
    submitHealthCardNumberToServer
} from "../../sagas/ApplicationAsyncSagas/actions";
import { ApplicationState } from "../../store";
import {
    changeHealthCardMaskLocation,
    changeHealthCardNumber,
    changeValidationPostalCodeValue,
    incrementNumberOfSubmitAttempts,
    resetAcquireMethodData,
    selectHealthCardAcquiringMode,
    toggleValidationMessageVisibility
} from "../../store/AcquireHealthCardNumber/actions";
import {
    getHealthCardNumberAcquisitionMode,
    getIncorrectRegionCodeDetected,
    getInvalidHealthCardNumberDetected,
    getNumberOfRegionCodeNumberOfSubmitAttempts,
    getShowPostalCodeVerificationModal
} from "../../store/AcquireHealthCardNumber/selectors";
import { ACQUIRING_MODE } from "../../store/AcquireHealthCardNumber/type";
import {
    getClinicCountry,
    getClinicRegion,
    getHealthCardParsers
} from "../../store/ClinicInformation/selectors";
import { showErrorUpdateMessage } from "../../store/Errors/actions";
import {
    connectInputToKeyboard,
    disconnectKeyboard,
    setupEnterFunction
} from "../../store/Keyboard/actions";
import { getFocusedElementId } from "../../store/Keyboard/selectors";
import {
    getCountryListOptionsFromPatientInfo,
    getLanguageResourceGeneralStrings,
    getLanguageResourceCountryMappedStrings,
    getCountriesLanguageResourceSelectOptionsForClinicHealthCards,
    getRegionsLanguageResourceSelectOptionsForClinicHealthCards
} from "../../store/LanguageLocale/selector";
import { getIsUserAuthenticated } from "../../store/Login/selectors";
import { getRegionCodeArray } from "../../store/PatientInformationForm/selectors";
import { AcquireHealthCardNumber } from "./AcquireHealthCardNumber.component";
import {
    IAcquireHealthCardNumberMapDispatchProps,
    IAcquireHealthCardNumberMapStateProps
} from "./Types";
import { getLoadingSpinnerVisibilityState } from "../../store/LoadingSpinner/selectors";
import { ErrorCodes, TypeOfFinalTransaction, HealthCardAcquisitionType, ClientEventScreen } from "../../dtos/KioskDtos.dtos";
import { redirectToSurvey } from "../../sagas/surveyRedirectSaga";
import { push } from "../../store/Routing/actions";

// Map the state values we want to the components props.
const mapStateToProps = (state: ApplicationState): IAcquireHealthCardNumberMapStateProps => ({
    /*TODO:Not used yet */
    // Boolean that will allow the user to see a confirmation modal when no health card is found on server side and no error is found.
    // Not used yet createNewPatientModalVisible: state.acquireHealthCardNumber.showCreateNewPatientModal,
    // Either creating new patient or updating existing info. If value is NONE, there is an error.    
    // currentPatientInfoMode: state.patientInformationForm.patientInterfaceForm.currentPatientInfoMode,
    healthCardParsers: getHealthCardParsers(state.clinicInformation),
    postalCodeEntered: state.acquireHealthCardNumber.postalCodeEntered,
    // Barcode scanner or manual input of healthcard number
    acquiringMethod: getHealthCardNumberAcquisitionMode(state.acquireHealthCardNumber),
    // Is the loading spinner active when making calls to server
    isLoadingSpinnerActive: getLoadingSpinnerVisibilityState(state.loadingSpinner),
    // Contains interface string spefically for this feature
    localeString: getLanguageResourceGeneralStrings(state.localeText),
    countryMappedStrings: getLanguageResourceCountryMappedStrings(state.localeText),
    // Contains generic string reused throughout the application
    // Number of times the user has tried to submit his or her healthcard
    healthCardNumberOfAttempts: state.acquireHealthCardNumber.healthCardNumberOfAttempts,
    // List of provinces/states and countries
    countryList: getCountriesLanguageResourceSelectOptionsForClinicHealthCards(state),
    stateList: getRegionsLanguageResourceSelectOptionsForClinicHealthCards(state),
    // Number of times the user has tried to enter his or her postal code in the confirmation modal
    postalCodeNumberOfAttempts: getNumberOfRegionCodeNumberOfSubmitAttempts(state.acquireHealthCardNumber),
    // Contains a boolean value that will indicate if the healthCardNumber is invalid
    invalidNumberDetected: getInvalidHealthCardNumberDetected(state.acquireHealthCardNumber),
    // Boolean that will show the postal code confirmation modal after the user has inserted a valid healthcard number and the server has found it.
    showPostalCodeConfirmationModal: getShowPostalCodeVerificationModal(state.acquireHealthCardNumber),
    // The postal code received from the server and which will be used to confirm the user.
    regionCodesFromServer: getRegionCodeArray(state.patientInformationForm),
    // Used to control the validation message visibility for the postal code confirmation modal.
    isIncorrectPostalCode: getIncorrectRegionCodeDetected(state.acquireHealthCardNumber),
    // Is the initial login user valid.
    isUserAuthenticated: getIsUserAuthenticated(state.login),
    // Default country and region for this site
    defaultCountry: getClinicCountry(state.clinicInformation),
    defaultRegion: getClinicRegion(state.clinicInformation),
    countriesList: getCountryListOptionsFromPatientInfo(state.localeText, state.patientInformationForm),

    // Keyboard connection
    keyboardValue: state.keyboard.value,
    healthCardNumber: state.acquireHealthCardNumber.healthCardNumber,
    currentCountry: state.acquireHealthCardNumber.maskCountry,
    currentRegion: state.acquireHealthCardNumber.maskRegion,

    focusedElementId: getFocusedElementId(state.keyboard),

    healthCardManualEntryOnlyEnabled: state.clinicInformation.featureFlags.healthCardManualEntryOnlyEnabled
});

// Map the dispatch functions to the components props.
const mapDispatchToProps = (dispatch: Dispatch): IAcquireHealthCardNumberMapDispatchProps => ({
    resetData: () => dispatch(resetAcquireMethodData()),

    onHealthCardNumberChange: (healthCardNumber: string) => dispatch(changeHealthCardNumber(healthCardNumber)),

    onPostalCodeChange: (postalCode: string) => dispatch(changeValidationPostalCodeValue(postalCode)),

    return: () => dispatch(cancelPostalCodeConfirmation()),

    changeAcquiringMode: (mode: ACQUIRING_MODE) => {
        dispatch(disconnectKeyboard());
        dispatch(selectHealthCardAcquiringMode(mode));
    },

    cancelPostalCodeVerification: () => dispatch(redirectToSurvey(cancelPostalCodeConfirmation(), TypeOfFinalTransaction.CancelledOnTheirOwn, ClientEventScreen.HealthCardManualEntryPostalCodeVerification)),

    showAndUpdateErrorMessage: (errorCode: ErrorCodes) => dispatch(redirectToSurvey(showErrorUpdateMessage(true, errorCode, true), TypeOfFinalTransaction.EncounteredError, ErrorCodes.PostalCodeCheckUnsuccessful)),

    onSubmitIncrement: (fieldType: string) => dispatch(incrementNumberOfSubmitAttempts(fieldType)),

    showValidationMessage: (value: boolean, fieldType: string) => dispatch(toggleValidationMessageVisibility(value, fieldType)),

    submitHealthCardNumberToServer: (healthCardNumber: string, acquisitionType: HealthCardAcquisitionType) => { dispatch(submitHealthCardNumberToServer(healthCardNumber, acquisitionType)) },

    returnToLoginPage: () => dispatch(push(Routes.root)),

    postalCodeAuthenticatedDispatch: () => dispatch(finishedPostalCodeConfirmation()),

    showKeyboard: (pipe: (value: any) => void, value = "", mask: string = "", id: string) => dispatch(connectInputToKeyboard(pipe, value, mask, id, KeyboardType.NO_SPECIALS_ALL_CAPS)),

    changeCountryAndRegion: (country: string, region: string) => dispatch(changeHealthCardMaskLocation(country, region)),

    hideKeyboard: () => dispatch(disconnectKeyboard()),

    setupEnterFunction: (func: () => void) => dispatch(setupEnterFunction(func))
});

export default connect<IAcquireHealthCardNumberMapStateProps, IAcquireHealthCardNumberMapDispatchProps, void>(mapStateToProps, mapDispatchToProps)(AcquireHealthCardNumber);



// WEBPACK FOOTER //
// ./src/components/AcquireHealthCardNumber/AcquireHealthCardNumber.container.ts